import React from 'react';
import {
  fetchEarningsBySymbol,
  fetchDividendsBySymbol,
  fetchSplitsBySymbol,
  fetchIPO,
  fetchEconomicEventsBySymbol,
} from 'services/server/stockApi';

import EarningsCalendarPage from 'screens/Calendar/EarningsCalendarPage';
import DividendsCalendarPage from 'screens/Calendar/DividendsCalendarPage';
import SplitsCalendarPage from 'screens/Calendar/SplitsCalendarPage';
import IPOCalendarPage from 'screens/Calendar/IPOCalendarPage';
import EconomicEventsPage from 'screens/Calendar/EconomicEventsCalendarPage';

export type CalendarSlug = 'earnings' | 'dividends' | 'splits' | 'ipos' | 'economic-events';

interface OptimizedCalendarProps {
  data: any[];
  dynamicData?: any[];
  setDynamicData?: (data: any[]) => void;
  isLoadingDynamic?: boolean;
  setIsLoadingDynamic?: (loading: boolean) => void;
  slug?: string;
  symbol?: string | null | undefined;
}

export const slugToComponentMap: Record<CalendarSlug, React.FC<OptimizedCalendarProps>> = {
  earnings: EarningsCalendarPage,
  dividends: DividendsCalendarPage,
  splits: SplitsCalendarPage,
  ipos: IPOCalendarPage,
  'economic-events': EconomicEventsPage,
};

export const slugToFetcherMap: Record<CalendarSlug, (symbol: string) => Promise<any[]>> = {
  earnings: async (symbol: string) => {
    try {
      return await fetchEarningsBySymbol(symbol);
    } catch (error) {
      console.error(`Failed to fetch earnings for ${symbol}:`, error);
      return [];
    }
  },
  dividends: async (symbol: string) => {
    try {
      return await fetchDividendsBySymbol(symbol);
    } catch (error) {
      console.error(`Failed to fetch dividends for ${symbol}:`, error);
      return [];
    }
  },
  splits: async (symbol: string) => {
    try {
      return await fetchSplitsBySymbol(symbol);
    } catch (error) {
      console.error(`Failed to fetch splits for ${symbol}:`, error);
      return [];
    }
  },
  ipos: async () => {
    try {
      return await fetchIPO();
    } catch (error) {
      console.error('Failed to fetch IPOs:', error);
      return [];
    }
  },
  'economic-events': async (symbol: string) => {
    try {
      return await fetchEconomicEventsBySymbol(symbol);
    } catch (error) {
      console.error(`Failed to fetch economic events for ${symbol}:`, error);
      return [];
    }
  },
};

// Calendar metadata for better organization
export const CALENDAR_METADATA: Record<
  CalendarSlug,
  {
    name: string;
    description: string;
    supportsSymbolFiltering: boolean;
    updateFrequency: 'realtime' | 'hourly' | 'daily';
    category: 'financial' | 'corporate' | 'economic';
  }
> = {
  earnings: {
    name: 'Earnings',
    description: 'Quarterly earnings announcements and results',
    supportsSymbolFiltering: true,
    updateFrequency: 'daily',
    category: 'financial',
  },
  dividends: {
    name: 'Dividends',
    description: 'Dividend announcements and payment dates',
    supportsSymbolFiltering: true,
    updateFrequency: 'daily',
    category: 'financial',
  },
  splits: {
    name: 'Stock Splits',
    description: 'Stock split announcements and effective dates',
    supportsSymbolFiltering: true,
    updateFrequency: 'daily',
    category: 'corporate',
  },
  ipos: {
    name: 'IPOs',
    description: 'Initial public offerings and market debuts',
    supportsSymbolFiltering: false,
    updateFrequency: 'daily',
    category: 'corporate',
  },
  'economic-events': {
    name: 'Economic Events',
    description: 'Economic indicators and government data releases',
    supportsSymbolFiltering: false,
    updateFrequency: 'realtime',
    category: 'economic',
  },
};

export const getCalendarMetadata = (slug: CalendarSlug) => {
  return CALENDAR_METADATA[slug];
};

export const getSymbolSupportedCalendars = (): CalendarSlug[] => {
  return Object.entries(CALENDAR_METADATA)
    .filter(([, metadata]) => metadata.supportsSymbolFiltering)
    .map(([slug]) => slug as CalendarSlug);
};

export const getCalendarByCategory = (
  category: 'financial' | 'corporate' | 'economic',
): CalendarSlug[] => {
  return Object.entries(CALENDAR_METADATA)
    .filter(([, metadata]) => metadata.category === category)
    .map(([slug]) => slug as CalendarSlug);
};

export const isValidCalendarSlug = (slug: string): slug is CalendarSlug => {
  return Object.keys(slugToComponentMap).includes(slug as CalendarSlug);
};

export const supportsSymbolFiltering = (slug: CalendarSlug): boolean => {
  return CALENDAR_METADATA[slug].supportsSymbolFiltering;
};
